import * as React from "react";
import TextField, { TextFieldLabel } from "../core/TextField";
import PageContainer from "../core/PageContainer";
import Breadcrumbs from "../core/Breadcrumbs";
import Card from "../core/Card";
import {
  getCompanyCwUrl,
  getEmailFromContact,
  getFullNameFromContact,
  getPrimaryContact,
} from "../../libs/chartec_cw";
import Panel from "../core/Panel";
import PickCwCompany from "../org/PickCwCompany";
import PickCwContact from "../org/PickCwContact";
import Select from "react-select";
import CenterDiv from "../core/CenterDiv";
import { createOrg } from "../../libs/organization";
import { toast } from "../../utils";
import { wrapComponentForErrors } from "../utility/ErrorHandler";
import jfetch from "../../utils/jfetch";
import * as Sentry from "@sentry/browser";
import {useState, useEffect} from "react";
import { setgroups } from "process";

type MembershipTypeOption = ISelectOption<"classic" | "premium" | "legacy" | "a_la_carte">;
type PurchaseStrategyOption = ISelectOption<true | false>;

interface IProps {}

interface IState {
  selectedCompany?: ICtCompanySearchResult;
  selectedOwner?: ICtGetContactsResult;
  membershipType: MembershipTypeOption;
  skipOnboarding: ISelectOption;
  purchasedStrategySession: PurchaseStrategyOption;
  creating: boolean;
  primaryAssign: boolean;
  stepsIsHidden: boolean;
  checkedSteps?: Array<string>;
  pgroup: string;
}

const skipOnboardingOption = { No: 0, Yes: 1, Partial: 2 };

const checkboxes = [
  {
    key: "settings",
    label: "Settings",
    checked: true,
    readonly: true,
  },
  {
    key: "invites",
    label: "Invites",
    checked: true,
    readonly: true,
  },
  {
    key: "companyInfo",
    label: "Company Info",
    checked: true,
    readonly: true,
  },
  {
    key: "strategySession",
    label: "Strategy Session",
    checked: false,
    readonly: false,
  },
  {
    key: "disc",
    label: "Disc Assessment",
    checked: false,
    readonly: false,
  },
  {
    key: "hardware",
    label: "Hardware",
    checked: true,
    readonly: true,
  },
  {
    key: "done",
    label: "Done",
    checked: true,
    readonly: true,
  },
];

class OrganizationNew extends React.Component<IProps, IState> {
  state: Readonly<IState> = {
    membershipType: { label: "Classic", value: "classic" },
    skipOnboarding: { label: "No", value: skipOnboardingOption["No"] },
    purchasedStrategySession: { label: "No", value: false },
    creating: false,
    primaryAssign: false,
    stepsIsHidden: true,
    checkedSteps: [
      "settings",
      "invites",
      "companyInfo",
      "hardware",
      "strategySession",
      "disc",
      "done",
    ],
    pgroup:'1'
  };

  onCreate = async () => {
    this.setState({ creating: true });

    let {
      selectedCompany,
      selectedOwner,
      membershipType,
      skipOnboarding,
      purchasedStrategySession,
      checkedSteps,
      pgroup
    } = this.state;

    if (!selectedCompany || !selectedOwner) {
      throw "Missing selected company or owner, something bad happened.";
    }

    //write conondition to tie StrategySession var to new checklist
    if (this.isChecked({ key: "strategySession" })) {
      purchasedStrategySession = { label: "Yes", value: true };
    } else {
      purchasedStrategySession = { label: "No", value: false };
    }

    const ownerEmail = getEmailFromContact(selectedOwner);
    if (!ownerEmail) {
      this.setState({ creating: false });
      toast.error(
        "Unable to create organization, owner is missing email address"
      );
      throw "Missing owner email, something bad happened.";
    }

    let result;
    try {
      result = await createOrg({
        name: selectedCompany.name,
        cwRecid: selectedCompany.recid,
        cwCompanyid: selectedCompany.companyid,
        ownerName: getFullNameFromContact(selectedOwner),
        ownerEmail,
        ownerTitle: selectedOwner.title,
        membershipType: membershipType.value,
        skipOnboarding: skipOnboarding.value,
        purchasedStrategySession: purchasedStrategySession.value,
        checkedSteps: checkedSteps,
        pgroup: +pgroup
      });
    } catch (e) {
      toast.error(
        "An error occurred while creating the owner, contact devteam for more info."
      );
      Sentry.captureException(e);
      this.setState({ creating: false });
      return;
    }

    if (result) {
      toast.success("Organization created!");
      Turbolinks.visit(result.organization.links.url);
    }
    this.setState({ creating: false });
  };

  checkCwCompany = async opt => {
    this.setState({ creating: true });
    let result = await jfetch.get(`/connectwise_members/${opt.value.recid}`);
    if (result.data && result.data.length === 0) {
      this.setState({
        selectedCompany: opt.value,
      });
      this.selectPrimaryContact(opt.value);
    } else {
      toast.error(
        "Connectwise Member is associated with an existing organization"
      );
    }
    this.setState({ creating: false });
  };

  selectPrimaryContact = async selectedCompany => {
    const result = await getPrimaryContact(selectedCompany.recid.toString());
    // Only set primary contact if there's a result and they have a valid email address
    if (
      result &&
      result.contacts &&
      result.contacts.length > 0 &&
      getEmailFromContact(result.contacts[0])
    ) {
      this.setState({
        selectedOwner: result.contacts[0],
        primaryAssign: true,
      });
    }
  };

  handleCheckbox = (e, item) => {
    let newCheckboxList = [...this.state.checkedSteps];
    if (e.target.checked) {
      newCheckboxList.push(item.key);
      this.setState({ checkedSteps: newCheckboxList });
    } else {
      const filteredList = newCheckboxList.filter(step => step != item.key);
      this.setState({ checkedSteps: filteredList });
    }
  };

  isChecked = item => {
    return this.state.checkedSteps.includes(item.key);
  };

  onboardChange = opt => {
    if (opt.value == 2) {
      //set steps to partial complete
      let newCheckboxList = [
        "settings",
        "invites",
        "companyInfo",
        "hardware",
        "done",
      ];
      this.setState({ checkedSteps: newCheckboxList, stepsIsHidden: false });
      // show approved steps
    } else {
      let newCheckboxList = [
        "settings",
        "invites",
        "companyInfo",
        "hardware",
        "strategySession",
        "disc",
        "done",
      ];
      this.setState({ checkedSteps: newCheckboxList, stepsIsHidden: true });
    }
  };

  switchGroup = id => {
    this.setState({pgroup:id})
  }

  render() {
    const {
      selectedCompany,
      selectedOwner,
      membershipType,
      skipOnboarding,
      primaryAssign,
      purchasedStrategySession,
    } = this.state;

    return (
      <PageContainer>
        <Breadcrumbs
          crumbs={[
            { label: "Organizations", route: "/organizations" },
            { label: "New Org" },
          ]}
        />
        <Card title={"New Organization"}>
          <Panel heading={"Creating a New Organization"} type={"primary"}>
            <p>
              Let's create a new organization! It should exist in ConnectWise
              already, so let's find it. Use the search box below, and choose a
              company. You'll be able to verify if it's the correct company
              before proceeding.
            </p>

            <div className="columns is-centered">
              <div className="column is-6">
                <PickCwCompany
                  onSelect={opt =>
                    opt
                      ? this.checkCwCompany(opt)
                      : this.setState({
                          selectedCompany: undefined,
                          selectedOwner: undefined,
                        })
                  }
                />
              </div>
            </div>
          </Panel>
          {selectedCompany && (
            <Panel
              heading={"Verify Company & Choose an Owner"}
              type={"primary"}
            >
              <p>
                You've chosen <strong>{selectedCompany.name}</strong> with a
                RecID of <strong>{selectedCompany.recid}</strong>. To make sure
                this is the correct company in ConnectWise,{" "}
                <a
                  target={"_blank"}
                  rel="noopener noreferrer"
                  href={getCompanyCwUrl(selectedCompany.recid)}
                >
                  click here
                </a>
                .
              </p>
              <p>
                {primaryAssign
                  ? "Primary contact has been assigned as owner, choose unset if you want to change assignment"
                  : "Next, let's choose an owner. You can use the fields below to\n" +
                    "                narrow the search if necessary."}
              </p>
              {selectedOwner ? (
                <table className={"table"}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Title</th>
                      <th>Email</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{getFullNameFromContact(selectedOwner)}</td>
                      <td>{selectedOwner.title}</td>
                      <td>{getEmailFromContact(selectedOwner)}</td>
                      <td>
                        <button
                          className="btn btn-theme-danger"
                          onClick={() =>
                            this.setState({ selectedOwner: undefined })
                          }
                        >
                          Unset
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <PickCwContact
                  onSelect={o => {
                    if (getEmailFromContact(o)) {
                      this.setState({ selectedOwner: o });
                    } else {
                      toast.error(
                        "Unable to assign owner without email address. Set email address in connectwise for this user"
                      );
                    }
                  }}
                  companyRecId={selectedCompany.recid}
                />
              )}
            </Panel>
          )}
          {selectedCompany && selectedOwner && (
            <>
              <Panel heading={"Organization Flags"} type={"info"}>
                <p>Next, you may set some organization flags.</p>
                <p>Please ensure the membership type is correct.</p>
                <p>
                  You may have the organization skip the onboarding process if
                  they have already completed it externally.
                </p>
                <div className="columns">
                  <div className="column">
                    <TextFieldLabel label={"Skip Onboarding"} />
                    <Select
                      value={skipOnboarding}
                      options={[
                        { label: "Yes", value: skipOnboardingOption["Yes"] },
                        { label: "No", value: skipOnboardingOption["No"] },
                        {
                          label: "Partial (Specify Steps)",
                          value: skipOnboardingOption["Partial"],
                        },
                      ]}
                      searchable={false}
                      clearable={false}
                      onChange={(opt: ISelectOption<number>) => {
                        this.setState({ skipOnboarding: opt });
                        this.onboardChange(opt);
                      }}
                    />
                  </div>
                  <div className="column">
                    <TextFieldLabel label={"Permissions Group"} />
                    {/* <Select
                      value={membershipType}
                      options={[
                        { label: "Classic", value: "classic" },
                        { label: "Premium", value: "premium" },
                        { label: "Legacy", value: "legacy" },
                        { label: "A La Carte", value: "a_la_carte"},
                      ]}
                      searchable={false}
                      clearable={false}
                      onChange={(opt: MembershipTypeOption) =>
                        this.setState({ membershipType: opt })
                      }
                    /> */}
                    <Groups switchGroup={this.switchGroup} />
                  </div>
                  <div className="column">
                    {!this.state.stepsIsHidden && (
                      <>
                        <TextFieldLabel label={"Approved Steps"} />
                        {checkboxes.map(item => (
                          <div
                            key={item.key}
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              name="steps"
                              value={item.key}
                              checked={this.isChecked(item)}
                              disabled={item.readonly}
                              onChange={e => this.handleCheckbox(e, item)}
                              style={{ height: "25px", margin: "0px" }}
                            />{" "}
                            <p
                              style={{
                                margin: "0px",
                                marginLeft: "5px",
                                fontWeight: "bold",
                              }}
                            >
                              {item.label}
                            </p>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </Panel>
              <Panel heading={"Review & Create Organization"} type={"success"}>
                <p>
                  Review the information we'll be using to create the
                  organization and click create below.
                </p>
                <CenterDiv>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Organization Name</strong>
                        </td>
                        <td>{selectedCompany.name}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Organization CW RecID</strong>
                        </td>
                        <td>{selectedCompany.recid}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Owner Name</strong>
                        </td>
                        <td>{getFullNameFromContact(selectedOwner)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Owner Title</strong>
                        </td>
                        <td>{selectedOwner.title}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Owner Email</strong>
                        </td>
                        <td>{getEmailFromContact(selectedOwner)}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Membership Type</strong>
                        </td>
                        <td>{membershipType.label}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Skip Onboarding</strong>
                        </td>
                        <td>{skipOnboarding.label}</td>
                      </tr>
                    </tbody>
                  </table>
                </CenterDiv>
                <CenterDiv>
                  <button
                    className={"btn btn-theme-primary btn-lg"}
                    onClick={this.onCreate}
                  >
                    Create Organization
                  </button>
                </CenterDiv>
              </Panel>
            </>
          )}
        </Card>
      </PageContainer>
    );
  }
}

function Groups(props){
  const [groups, setGroups] = useState([])
  useEffect(() => {
    jfetch.get('/permissions/groups')
    .then((resp) => {
      let gs = []
      resp.data.forEach(group => {
        gs.push(group.attributes)
      });
      setGroups(gs)
    })
  }, [])
  return (
    <select className="Select-control" onChange={(e) => props.switchGroup(e.target.value)}>
      { groups.map((group) => {return <option key={group.id} value={group.id}>{group.name}</option>}) }
    </select>
  )
}

export default wrapComponentForErrors(OrganizationNew);



// WEBPACK FOOTER //
// ./app/javascript/components/pages/OrganizationNew.tsx